import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Loader2, Tag, MoreHorizontal, Pencil, Trash2, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const PRESET_COLORS = [
  '#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6',
  '#06b6d4', '#ef4444', '#ec4899', '#6366f1',
  '#f97316', '#14b8a6', '#a855f7', '#84cc16',
];

// Add/Edit Service Type Dialog
function ServiceTypeDialog({ open, onOpenChange, onSuccess, editService = null, existingServices = [] }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
  });

  useEffect(() => {
    if (editService) {
      setFormData({
        name: editService.name || '',
        description: editService.description || '',
        color: editService.color || '#3b82f6',
      });
    } else {
      // Pick first available color automatically
      const usedColors = existingServices.map(s => s.color);
      const firstFree = PRESET_COLORS.find(c => !usedColors.includes(c)) || '#3b82f6';
      setFormData({ name: '', description: '', color: firstFree });
    }
    setError('');
  }, [editService, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        color: formData.color,
      };
      if (editService) {
        await api.patch(`/service-types/${editService.id}`, payload);
      } else {
        await api.post('/service-types', payload);
      }
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  // Colors used by OTHER services (not the one being edited)
  const usedColors = existingServices
    .filter(s => editService ? s.id !== editService.id : true)
    .map(s => s.color);

  const availableColors = PRESET_COLORS.filter(c => !usedColors.includes(c));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-auto bottom-0 translate-x-0 translate-y-0 sm:top-[50%] sm:translate-y-[-50%] sm:translate-x-[-50%] sm:bottom-auto left-0 right-0 sm:left-[50%] sm:right-auto w-full sm:w-auto sm:max-w-md rounded-t-2xl rounded-b-none sm:rounded-lg max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{editService ? 'Редактировать услугу' : 'Новая услуга'}</DialogTitle>
          <DialogDescription>
            {editService ? 'Измените параметры услуги' : 'Создайте новый тип услуги'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
            )}

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Название *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Уборка квартиры"
                required
                data-testid="service-name"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Описание</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Стандартная уборка жилого помещения..."
                rows={3}
                data-testid="service-description"
              />
            </div>

            {/* Color — unique per service */}
            <div className="space-y-2">
              <Label>Цвет в календаре</Label>
              <div className="flex flex-wrap items-center gap-2">
                {availableColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      "w-8 h-8 rounded-full border-2 transition-transform hover:scale-110",
                      formData.color === color ? "border-foreground scale-110" : "border-transparent"
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({...formData, color})}
                  />
                ))}
                {/* Currently selected color (may not be in available list if custom) */}
                {!availableColors.includes(formData.color) && (
                  <button
                    type="button"
                    className="w-8 h-8 rounded-full border-2 border-foreground scale-110"
                    style={{ backgroundColor: formData.color }}
                  />
                )}
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  className="w-10 h-8 p-0.5 cursor-pointer"
                  title="Произвольный цвет"
                />
              </div>
              {usedColors.length > 0 && (
                <p className="text-xs text-muted-foreground">Занятые цвета скрыты — каждая услуга имеет уникальный цвет</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
            <Button type="submit" disabled={loading} data-testid="service-submit">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
              {editService ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Main Services Page
export default function ServicesPage() {
  const { api } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editService, setEditService] = useState(null);

  const fetchServices = useCallback(async () => {
    try {
      const response = await api.get('/service-types?is_active=true');
      setServices(response.data);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleEdit = (service) => {
    setEditService(service);
    setDialogOpen(true);
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`Вы уверены, что хотите удалить услугу "${service.name}"?`)) return;
    
    try {
      await api.delete(`/service-types/${service.id}`);
      fetchServices();
    } catch (err) {
      console.error('Failed to delete service:', err);
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) setEditService(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Услуги</h1>
          <p className="text-muted-foreground mt-1">
            Управление типами услуг
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-service-btn">
          <Plus className="mr-2 h-4 w-4" />
          Новая услуга
        </Button>
      </div>

      {/* Services Grid - Mobile Friendly */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : services.length === 0 ? (
        <Card className="cgc-card">
          <CardContent className="p-12 text-center text-muted-foreground">
            <Tag className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Нет услуг</p>
            <Button 
              variant="link" 
              onClick={() => setDialogOpen(true)}
              className="mt-2"
            >
              Создать первую услугу
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map(service => (
            <Card 
              key={service.id} 
              className="cgc-card hover:shadow-md transition-shadow"
              data-testid={`service-card-${service.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: service.color + '20' }}
                    >
                      <Tag className="h-5 w-5" style={{ color: service.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{service.name}</h3>
                      {service.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                          {service.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(service)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Редактировать
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(service)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Удалить
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {service.description && (
                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t line-clamp-2">{service.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <ServiceTypeDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        onSuccess={fetchServices}
        editService={editService}
        existingServices={services}
      />
    </div>
  );
}
